import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { z } from "zod";
import {
  type ContentRootProps,
  languageKeys,
  type ContentComponentConfig,
  type Data,
  type Excursion,
} from "@op/puck-types";
import { createServerFn } from "@tanstack/react-start";
import { rendererClient } from "../../../../../lib/rendererClient";
import { Renderer } from "../../../../../components/Renderer";
import { ThemeFooter } from "@op/puck";
import { OldVersionNotification } from "@op/sf-op";
import { LinkButton } from "@op/sf-op";
import { FeatherArrowRight } from "@subframe/core";
import {
  makeRendererImageUrl,
  RenderProvider,
  TranslationProvider,
} from "@op/utils";
import { publicEnv } from "../../../../../utils/env";
import { getRequest } from "@tanstack/react-start/server";
import { Trans, useLingui } from "@lingui/react/macro";
import { useTranslations } from "../../../../../utils/translations";

const contextSchema = z.object({
  organizationSlug: z.string(),
  projectSlug: z.string(),
});

const paramsSchema = z.object({
  languageKey: z.enum(languageKeys),
  topicSlug: z.string(),
  pageSlug: z.string(),
  versionId: z.string(),
});

const getVersion = createServerFn({ method: "GET" })
  .inputValidator(z.object({ params: paramsSchema, context: contextSchema }))
  .handler(
    async ({
      data: {
        params: { languageKey, topicSlug, pageSlug, versionId },
      },
    }) => {
      const req = getRequest();
      const res = await rendererClient.versions[":languageKey"][":topicSlug"][
        ":pageSlug"
      ][":versionId"].$get({
        param: {
          languageKey,
          topicSlug,
          pageSlug,
          versionId,
        },
      });
      if (!res.ok) {
        if (res.status === 404) throw notFound();
        else throw new Error("Failed to fetch version");
      }
      const data = await res.json();

      const pdfUrl = new URL(
        `/${languageKey}/${topicSlug}/${pageSlug}/${versionId}/pdf`,
        req.url,
      ).toString();
      return { ...data, hostname: new URL(req.url).hostname, pdfUrl };
    },
  );

export const Route = createFileRoute(
  "/$languageKey/$topicSlug/$pageSlug/$versionId/",
)({
  component: RouteComponent,
  params: paramsSchema,
  loader: async ({ params, context }) =>
    await getVersion({
      data: {
        params,
        context,
      },
    }),
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { meta, hostname } = loaderData;

    const headContent: Record<string, any> = {};

    const metaContent: Record<string, string>[] = [];
    metaContent.push({
      title: `${meta.name} | ${meta.projectName} | ${meta.organizationName}`,
    });
    if (meta.metaTitle) {
      metaContent.push({
        property: "og:title",
        content: meta.metaTitle,
      });
    }
    if (meta.metaDescription) {
      metaContent.push({
        name: "description",
        content: meta.metaDescription,
      });
      metaContent.push({
        property: "og:description",
        content: meta.metaDescription,
      });
    }
    if (meta.metaImage) {
      metaContent.push({
        property: "og:image",
        content: makeRendererImageUrl(meta.metaImage, publicEnv.VITE_API_URL),
      });
    }
    if (!meta.indexed) {
      metaContent.push({
        name: "robots",
        content: "noindex, nofollow",
      });
    }

    const scriptContent: Record<string, string>[] = [];
    if (meta.analyticsEnabled) {
      scriptContent.push({
        "data-domain": hostname,
        src: "https://analytics.uninspired.studio/js/script.js",
      });
    }

    headContent.meta = metaContent;
    headContent.scripts = scriptContent;

    return headContent;
  },
});

function RouteComponent() {
  const {
    content,
    excursions,
    figures,
    outline,
    citations,
    glossaryItems,
    versions,
    meta,
    pdfUrl,
    copyrightText,
    imprintLink,
    privacyLink,
    hasImprint,
    hasPrivacyPolicy,
  } = Route.useLoaderData();
  const { renderMedium } = Route.useRouteContext();
  const { languageKey, topicSlug, pageSlug, versionId } = Route.useParams();

  const { t } = useLingui();
  const translations = useTranslations();

  return (
    <RenderProvider
      value={{
        context: "renderer",
        renderMedium: renderMedium as "print" | "screen",
        apiUrl: publicEnv.VITE_API_URL,
      }}
    >
      <div className="min-h-screen flex flex-col justify-between items-center">
        <div className="w-full">
          <Renderer
            content={content as Data<ContentComponentConfig, ContentRootProps>}
            excursions={excursions as Excursion[]}
            citations={citations}
            glossaryItems={glossaryItems}
            versions={versions}
            outline={outline}
            figures={figures}
            pdfUrl={pdfUrl}
            meta={{
              ...meta,
              publishedAt: meta.publishedAt ? new Date(meta.publishedAt) : null,
            }}
          />
        </div>
        {renderMedium !== "print" && (
          <TranslationProvider {...translations}>
            <ThemeFooter
              copyrightText={copyrightText ?? undefined}
              imprintLink={imprintLink ?? undefined}
              privacyLink={privacyLink ?? undefined}
              hasImprint={hasImprint}
              hasPrivacyPolicy={hasPrivacyPolicy}
              languageKey={languageKey}
              LinkComponent={Link}
            />
          </TranslationProvider>
        )}
      </div>
      {versionId !== meta.currentVersionId && (
        <OldVersionNotification
          message={t`You're viewing an old version of this page.`}
          action={
            <Link
              className="contents"
              to="/$languageKey/$topicSlug/$pageSlug/$versionId"
              params={{
                languageKey,
                topicSlug,
                pageSlug,
                versionId: meta.currentVersionId!,
              }}
            >
              <LinkButton
                disabled={false}
                variant="neutral"
                size="medium"
                icon={null}
                iconRight={<FeatherArrowRight />}
              >
                <Trans>Go to newest version</Trans>
              </LinkButton>
            </Link>
          }
        />
      )}
    </RenderProvider>
  );
}
